import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export interface ItemListEditorProps {
	/** Section header label */
	label: string
	/** Text shown in the "+ Add" button */
	addLabel: string
	/** Placeholder for each item input */
	placeholder: string
	/** Controlled list of string values */
	items: string[]
	/** Called with the full updated array on any change */
	onChange: (items: string[]) => void
	/**
	 * Minimum number of items to keep — the remove button is hidden when the
	 * list length equals this value. Defaults to 1.
	 */
	minItems?: number
}

export function ItemListEditor({
	label,
	addLabel,
	placeholder,
	items,
	onChange,
	minItems = 1,
}: ItemListEditorProps) {
	const add = () => onChange([...items, ''])

	const remove = (index: number) =>
		onChange(items.filter((_, i) => i !== index))

	const update = (index: number, value: string) =>
		onChange(items.map((item, i) => (i === index ? value : item)))

	return (
		<div className="space-y-3">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{label}
					</span>
					<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-kaefer-medium-grey/15 px-1.5 text-[11px] font-semibold tabular-nums text-kaefer-medium-grey">
						{items.length}
					</span>
				</div>
				<Button
					type="button"
					variant="ghost"
					size="xs"
					onClick={add}
					className="gap-1.5 text-kaefer-medium-grey hover:text-kaefer-medium-grey/80"
				>
					<Plus className="size-3.5" />
					{addLabel}
				</Button>
			</div>

			{/* Item rows */}
			<div className="space-y-2">
				{items.map((item, i) => (
					<div key={i} className="flex items-center gap-2">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-kaefer-medium-grey/10 text-xs font-bold text-kaefer-medium-grey">
							{i + 1}
						</span>
						<Input
							value={item}
							onChange={(e) => update(i, e.target.value)}
							placeholder={placeholder}
							className="h-9 flex-1"
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									e.preventDefault()
									add()
								}
							}}
						/>
						{items.length > minItems && (
							<Button
								type="button"
								variant="ghost"
								size="icon-xs"
								onClick={() => remove(i)}
								className="shrink-0 text-muted-foreground hover:text-kaefer-red"
							>
								<X className="size-3.5" />
							</Button>
						)}
					</div>
				))}
			</div>
		</div>
	)
}
